// Champ de compte avec recherche (A1a, 02/11/2026) : la liste affiche
// « 411CARBL — Nom du client », « 401EDF — Nom du fournisseur », « 627800 —
// Intitulé », et « 411XXXX — (sans fiche) » pour un code sans fiche ; on cherche
// par code ou par nom (accents et majuscules ignorés). Le champ garde le code,
// le nom s'affiche à côté. Saisie libre conservée (un compte absent de la liste
// reste contrôlé par le serveur).
// A1b-2 (03/11/2026) : groupes (ex. « Clients (411) », « Autres opérations ») et
// groupe « autre sens », affiché seulement sur demande (remboursement, avoir).
import { useMemo, useRef, useState } from "react";

const norm = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
const KIND_ORDER = { client: 0, fournisseur: 0, general: 1, sans_fiche: 2 };
const PER_GROUP = 40;

export function choiceLabel(c) { return c ? `${c.account_code} — ${c.label}` : ""; }

/** Comptes qui correspondent à la recherche, triés (code, puis nom). */
function search(list, q) {
  if (!q) return list;
  const scored = [];
  for (const c of list) {
    const code = norm(c.account_code), name = norm(c.label);
    const s = code.startsWith(q) ? 0 : name.startsWith(q) ? 1 : name.includes(q) ? 2 : code.includes(q) ? 3 : -1;
    if (s >= 0) scored.push([s, KIND_ORDER[c.kind] ?? 1, c]);
  }
  return scored.sort((a, b) => a[0] - b[0] || a[1] - b[1] || String(a[2].account_code).localeCompare(String(b[2].account_code))).map(x => x[2]);
}

/**
 * @param groups      [{ title, test(c) }] : liste groupée (sinon liste simple de `choices`)
 * @param otherGroup  { title, test(c) } : groupe « autre sens », replié par défaut
 */
export default function AccountPicker({ value, choices = [], groups = null, otherGroup = null, onCommit, width = 90, disabled = false, ariaLabel = "Compte", placeholder = "Compte", showName = true, style }) {
  const [text, setText] = useState(null); // null : pas de saisie en cours
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [showOther, setShowOther] = useState(false);
  const inputRef = useRef(null);
  const byCode = useMemo(() => new Map(choices.map(c => [String(c.account_code).toUpperCase(), c])), [choices]);
  const current = text == null ? String(value || "") : text;
  const known = byCode.get(current.trim().toUpperCase());
  const q = text == null ? "" : norm(current.trim());
  // Sections affichées : groupes demandés (+ « autre sens » sur demande), sinon une seule liste
  const sections = useMemo(() => {
    const defs = groups ? [...groups, ...(otherGroup && showOther ? [{ ...otherGroup, other: true }] : [])] : [{ title: null, test: () => true }];
    return defs.map(g => ({ title: g.title, other: !!g.other, items: search(choices.filter(g.test), q).slice(0, groups ? PER_GROUP : 60) })).filter(s => s.items.length);
  }, [choices, groups, otherGroup, showOther, q]);
  const flat = sections.flatMap(s => s.items);

  const commit = (code) => {
    setOpen(false); setText(null);
    const v = String(code || "").trim();
    if (v && v !== String(value || "")) onCommit && onCommit(v);
  };

  return (
    <span style={{ position: "relative", display: "inline-block", ...style }}>
      <input ref={inputRef} type="text" value={current} disabled={disabled} aria-label={ariaLabel} placeholder={placeholder}
        title={known ? choiceLabel(known) : undefined} autoComplete="off"
        style={{ width, padding: "2px 6px", fontSize: 11, borderRadius: 4, border: "1px solid #d1d5db", background: "var(--card)", color: "var(--text)" }}
        onFocus={() => { setOpen(true); setActive(0); }}
        onChange={e => { setText(e.target.value); setOpen(true); setActive(0); }}
        onKeyDown={e => {
          if (e.key === "ArrowDown") { e.preventDefault(); setActive(a => Math.min(a + 1, flat.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
          else if (e.key === "Enter") { e.preventDefault(); commit(open && text != null && flat[active] ? flat[active].account_code : current); inputRef.current && inputRef.current.blur(); }
          else if (e.key === "Escape") { setText(null); setOpen(false); }
        }}
        onBlur={() => { if (text != null) commit(current); else setOpen(false); }} />
      {showName && known && <span style={{ fontSize: 10, color: known.kind === "sans_fiche" ? "#b45309" : "var(--text-muted)", marginLeft: 4 }}>— {known.label}</span>}
      {open && !disabled && (flat.length > 0 || otherGroup) && (
        <div role="listbox" style={{ position: "absolute", zIndex: 50, top: "100%", left: 0, minWidth: 300, maxWidth: 420, maxHeight: 280, overflowY: "auto",
          background: "var(--card, #fff)", border: "1px solid #d1d5db", borderRadius: 6, boxShadow: "0 6px 18px #0002", fontSize: 11.5 }}>
          {sections.map(sec => (
            <div key={sec.title || "liste"}>
              {sec.title && <div style={{ padding: "4px 8px", fontSize: 10.5, fontWeight: 700, color: sec.other ? "#b45309" : "#6b7280", background: "#f9fafb", textTransform: "uppercase" }}>{sec.title}</div>}
              {sec.items.map(c => {
                const i = flat.indexOf(c);
                return (
                  <div key={`${sec.title}-${c.account_code}`} role="option" aria-selected={i === active}
                    onMouseDown={e => { e.preventDefault(); commit(c.account_code); }}
                    onMouseEnter={() => setActive(i)}
                    style={{ padding: "4px 8px", cursor: "pointer", background: i === active ? "#e0f2fe" : "transparent", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{c.account_code}</span>
                    <span style={{ color: c.kind === "sans_fiche" ? "#b45309" : "var(--text-muted)" }}> — {c.label}</span>
                  </div>
                );
              })}
            </div>
          ))}
          {flat.length === 0 && <div style={{ padding: "6px 8px", color: "#6b7280" }}>Aucun compte de ce côté pour cette recherche.</div>}
          {otherGroup && (
            <div onMouseDown={e => { e.preventDefault(); setShowOther(v => !v); setActive(0); }}
              style={{ padding: "5px 8px", borderTop: "1px solid #e5e7eb", cursor: "pointer", color: "#b45309", fontSize: 11 }}>
              {showOther ? "▲ Masquer l'autre sens" : `▼ Autre sens : ${otherGroup.title}`}
            </div>
          )}
        </div>
      )}
    </span>
  );
}
